import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PageCTA from '../../components/feature/PageCTA';

export default function HowItWorks() {
  const intelligenceSystems = [
    'Expectation Modeling',
    'Cognitive Load Simulation',
    'Friction Point Indicators',
    'Emotional Flow Resonance',
    'Desire Trigger Scoring',
    'Category Standards Benchmarking',
    'Narrative & Value Alignment'
  ];

  const differentiators = [
    'No paid testers',
    'No personal interpretation',
    'No guesswork',
    'No subjective ratings',
    'Repeatable and consistent scoring',
    'Built from proven success structures',
    'Designed for any stage — idea to global scale'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            How CrowdRounds Evaluates Your App
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            A clear, bias-free evaluation system powered by structured intelligence, proven benchmarks, and consistent scoring logic.
          </p>
        </div>
      </section>

      {/* Simple 3-Step Process */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Our Process Is Straightforward and Precise
          </h2>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="relative">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Submit Your App
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Provide your app link, concept, or prototype details.
              </p>
              <p className="text-gray-600 leading-relaxed">
                No complex setup. No backend access required.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Intelligence Evaluation Begins
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Your app is processed through our multi-layer intelligence framework:
              </p>
              <ul className="space-y-2">
                {intelligenceSystems.map((system, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700 text-sm">{system}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4 text-sm italic">
                Each system reveals a different dimension of app viability.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Receive Your Report
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                You get a structured report aligned with your chosen tier.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Your insights highlight strengths, weaknesses, opportunities, and next steps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Works */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            A Smarter, More Reliable Way to Evaluate an App
          </h2>
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              Traditional beta testing relies on opinions, emotions, and inconsistent feedback.
            </p>
            <p>
              CrowdRounds instead relies on structured patterns proven across top-performing apps.
            </p>
            <div className="pt-6 space-y-3">
              <p className="font-semibold text-gray-900">Your results are not guesses.</p>
              <p className="font-semibold text-gray-900">Your results are not opinions.</p>
              <p className="font-semibold text-gray-900">
                Your results are generated from real best practices and benchmark indicators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes CrowdRounds Different */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            What Makes CrowdRounds Different
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="flex items-start bg-white border-2 border-teal-100 rounded-xl p-6 hover:border-teal-300 transition-colors">
                <i className="ri-shield-check-fill text-teal-500 text-2xl mr-4 flex-shrink-0"></i>
                <span className="text-lg text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <PageCTA 
        title="See How Your App Performs"
        description="Get clarity, direction, and an intelligence-backed roadmap."
        primaryText="Start Evaluation"
        primaryLink="/pricing"
      />

      <Footer />
    </div>
  );
}
